import { ApiResult } from "../shared/networking/api_result";
import HttpService from "../shared/networking/http_service";
import { getConfig } from "./config.service";
import {
  ICommitKnittingProgramUploadResult,
  IFileCheckResult,
  IKnittingProgramResponse,
  IKnittingProgramUploadSession,
  IKnittingProgramVersion,
  IRevertCommand,
  IRevertResult,
  IStageKnittingProgramResult,
} from "models/knittingProgramLibrary";
import { IGridifyQuery } from "models/common";
import { buildGridifyQueryString } from "shared/utils/gridify";

const UPLOADS_BASE = "/mdsapi/knittingProgramUploads";

class KnittingProgramServiceAPI extends HttpService {
  public async getPrograms(
    libraryId: string,
    query?: IGridifyQuery,
  ): Promise<ApiResult<IKnittingProgramResponse>> {
    const params = new URLSearchParams(buildGridifyQueryString(query).slice(1));
    params.append("libraryId", libraryId);
    return this.get<IKnittingProgramResponse>(
      `/mdsapi/knittingPrograms/list?${params.toString()}`,
    );
  }

  public async checkFileVersions(
    libraryId: string,
    hashes: Record<string, string>,
  ): Promise<ApiResult<IFileCheckResult[]>> {
    return this.postDataForData<Record<string, string>, IFileCheckResult[]>(
      `/mdsapi/knittingPrograms/check-versions?libraryId=${encodeURIComponent(libraryId)}`,
      hashes,
    );
  }

  public async startUpload(
    libraryId: string,
    comment: string,
  ): Promise<ApiResult<IKnittingProgramUploadSession>> {
    return this.postDataForData<
      { libraryId: string; comment: string },
      IKnittingProgramUploadSession
    >(`${UPLOADS_BASE}/start`, { libraryId, comment });
  }

  public async stageFiles(
    sessionId: string,
    files: File[],
    onUploadProgress?: (percent: number) => void,
  ): Promise<ApiResult<IStageKnittingProgramResult[]>> {
    const formData = new FormData();
    files.forEach((f) => formData.append("files", f));
    return this.uploadFileForm<IStageKnittingProgramResult[]>(
      `${UPLOADS_BASE}/${encodeURIComponent(sessionId)}/files`,
      formData,
      onUploadProgress,
    );
  }

  public async commitUpload(
    sessionId: string,
    discardFailedItems: boolean,
  ): Promise<ApiResult<ICommitKnittingProgramUploadResult>> {
    return this.postForData<ICommitKnittingProgramUploadResult>(
      `${UPLOADS_BASE}/${encodeURIComponent(sessionId)}/commit?discardFailedItems=${discardFailedItems}`,
    );
  }

  public async abortUpload(
    sessionId: string,
    force = false,
  ): Promise<ApiResult<void>> {
    return this.delete(
      `${UPLOADS_BASE}/${encodeURIComponent(sessionId)}?force=${force}`,
    );
  }

  public abortUploadOnUnload(sessionId: string): void {
    this.deleteOnUnload(
      `${UPLOADS_BASE}/${encodeURIComponent(sessionId)}?force=false`,
    );
  }

  public downloadProgram(id: string, version?: number): string {
    const versionParam = version !== undefined ? `?version=${version}` : "";
    return `/mdsapi/knittingPrograms/${encodeURIComponent(id)}/download${versionParam}`;
  }

  public downloadProgramBundle(id: string, version?: number): string {
    const versionParam = version !== undefined ? `?version=${version}` : "";
    return `/mdsapi/knittingPrograms/${encodeURIComponent(id)}/download-bundle${versionParam}`;
  }

  public async deleteProgram(id: string): Promise<ApiResult<void>> {
    return this.delete(`/mdsapi/knittingPrograms/${encodeURIComponent(id)}`);
  }

  public async transferProgramToDefaultLibrary(
    id: string,
  ): Promise<ApiResult<void>> {
    return this.postDataForData<void, void>(
      `/mdsapi/knittingPrograms/${encodeURIComponent(id)}/transfer-to-default-library`,
      undefined,
    );
  }

  public async getProgramHistory(
    id: string,
  ): Promise<ApiResult<IKnittingProgramVersion[]>> {
    return this.get<IKnittingProgramVersion[]>(
      `/mdsapi/knittingPrograms/${encodeURIComponent(id)}/history`,
    );
  }

  public async revertProgram(
    id: string,
    command: IRevertCommand,
  ): Promise<ApiResult<IRevertResult>> {
    return this.postDataForData<IRevertCommand, IRevertResult>(
      `/mdsapi/knittingPrograms/${encodeURIComponent(id)}/revert`,
      command,
    );
  }
}

const appConfig = getConfig();
const TimeoutMilliseconds = Number(appConfig.httpTimeoutMs);
const KnittingProgramService = new KnittingProgramServiceAPI(
  TimeoutMilliseconds,
);

export default KnittingProgramService;
